import { FiMic } from 'react-icons/fi';

export const usingExamplesData = {
  courseId: 'using-examples',
  courseType: 'speaking-course',
  title: 'Using Examples Effectively',
  icon: FiMic,
  overview: {
    impact: 'Concrete examples raise your Coherence & Cohesion and Fluency scores by showing the examiner you can develop ideas fully',
    focus: 'Phrases for introducing examples, choosing personal vs general examples, keeping examples relevant and concise',
    uses: 'Speaking Parts 1, 2, and 3 — especially Part 2 long turns and Part 3 opinion questions',
    duration: '25 minutes',
  },

  sections: [
    {
      title: 'Why Examples Matter',
      content: `<p>IELTS examiners assess how well you <strong>develop and support</strong> your ideas. A claim without evidence sounds hollow. Adding a concrete example transforms a Band 5–6 answer into a Band 7+ answer because it demonstrates you can extend your response naturally.</p>
<p>Consider the difference:</p>`,
      points: [
        'Without example: "I think technology has changed education." — vague, undeveloped.',
        'With example: "I think technology has changed education. For instance, in my university most lectures are now recorded and uploaded online, so students can review material at their own pace." — specific, convincing.',
        'Examples show the examiner you can think on your feet and connect abstract ideas to real life.',
        'Well-placed examples also buy you thinking time, keeping your fluency smooth.',
      ],
      examples: [
        { label: 'Weak', text: 'Travel is important. For example, travel is really important for many reasons.' },
        { label: 'Strong', text: 'Travel is important. For instance, when I visited Japan last year, I learned far more about the culture than I ever could from a textbook.' },
      ],
    },
    {
      title: 'Phrases for Introducing Examples',
      content: `<p>Varying your example-introduction phrases prevents repetition and shows lexical range — a key Band 7+ indicator. Choose phrases that match the formality of your point.</p>`,
      points: [
        'Avoid using "for example" in every sentence — the examiner notices repetition.',
        'Higher-band candidates mix formal and informal phrases naturally.',
        'Some phrases work better for personal stories; others suit general facts.',
      ],
      table: {
        headers: ['Phrase', 'Formality', 'Best For'],
        rows: [
          ['For example, ...', 'Neutral', 'Any context'],
          ['For instance, ...', 'Neutral', 'Any context'],
          ['A good example of this is ...', 'Neutral–Formal', 'Introducing a specific case'],
          ['To illustrate, ...', 'Formal', 'Written-style or Part 3 answers'],
          ['Take ... for example', 'Informal–Neutral', 'Conversational tone'],
          ['Such as ...', 'Neutral', 'Listing within a sentence'],
          ['In my own experience, ...', 'Informal', 'Personal anecdotes'],
          ['One case in point is ...', 'Formal', 'Emphasising a strong example'],
          ['To give you an idea, ...', 'Informal', 'Casual illustration'],
          ['A prime example would be ...', 'Formal', 'Highlighting the best case'],
        ],
      },
    },
    {
      title: 'Personal vs General Examples',
      content: `<p>Both types are valuable, but they serve different purposes. Part 2 rewards personal examples because you are telling <em>your</em> story. Part 3 often benefits from general examples because you are discussing broader issues.</p>`,
      points: [
        'Personal examples add authenticity: "When I was in high school, my teacher used group projects to encourage teamwork."',
        'General examples add authority: "Research has shown that students who collaborate tend to perform better academically."',
        'The strongest answers blend both: state a general point, then illustrate with a personal experience.',
        'Avoid fabricating statistics — the examiner cares about language quality, not factual precision.',
      ],
      examples: [
        { label: 'Personal', text: 'In my family, we always discuss the news at dinner, which helped me become more aware of current events.' },
        { label: 'General', text: 'In many countries, families use mealtimes as an opportunity to share information and bond.' },
      ],
    },
    {
      title: 'Common Mistakes',
      content: `<p>Even confident speakers fall into traps when giving examples. Recognising these patterns helps you self-correct during the exam.</p>`,
      points: [
        '"For example, for example" — repeating the same phrase back-to-back signals limited vocabulary.',
        'Overly long examples that drift off-topic — keep examples to 1–2 sentences.',
        'Examples that don\'t match the claim — make sure your example actually supports your point.',
        'Using "like" repeatedly instead of varied phrases — "like" is acceptable once, but overuse sounds informal.',
        'Giving an example without first stating the main idea — always lead with your point.',
      ],
      warning: 'Never start your answer with an example. State your opinion or main idea first, then support it with an example.',
    },
  ],

  exercises: [
    {
      title: 'Shadowing (0.8×)',
      type: 'writing',
      instructions: 'Repeat the model slowly, then type the example you said.',
      questions: [
        {
          text: 'Model: "For example, last week I helped my brother with his homework." Say it twice at a slow pace, then write it.',
          example: 'For example, last week I helped my brother with his homework.',
          tip: 'Keep "For example" + one short story. Do not add a second story yet.',
        },
      ],
    },
    {
      title: 'Choose the Best Example Phrase',
      type: 'select',
      instructions: 'Select the most appropriate phrase to introduce an example in each context.',
      questions: [
        {
          text: 'In a formal Part 3 discussion about education policy, which phrase fits best? "_____, Finland\'s education system focuses on student well-being rather than standardised testing."',
          options: ['Like,', 'A case in point is', 'So basically,', 'You know,'],
          answer: 'A case in point is',
          explanation: '"A case in point is" suits the formal register of a Part 3 policy discussion. "Like" and "you know" are too informal; "so basically" is a filler, not an example phrase.',
        },
        {
          text: 'You want to add a casual personal story in Part 1. Which phrase is most natural? "_____, when I was younger, I used to collect stamps."',
          options: ['To illustrate,', 'One case in point is', 'In my own experience,', 'A prime example would be'],
          answer: 'In my own experience,',
          explanation: '"In my own experience" is ideal for a relaxed personal anecdote in Part 1. The other options are too formal for casual conversation.',
        },
        {
          text: 'You are listing several items in one sentence: "Many sports require teamwork, _____ football, basketball, and rugby."',
          options: ['for instance', 'a good example of this is', 'such as', 'to give you an idea'],
          answer: 'such as',
          explanation: '"Such as" is the correct phrase for listing items within a sentence. The others would require a separate clause.',
        },
        {
          text: 'You want to highlight one especially strong example in Part 3. "_____ the way Singapore transformed its education system in just two decades."',
          options: ['Like', 'A prime example would be', 'For example for example', 'Such as'],
          answer: 'A prime example would be',
          explanation: '"A prime example would be" adds emphasis and formality, perfect for highlighting a standout case in Part 3.',
        },
      ],
    },
    {
      title: 'Insert an Example Phrase',
      type: 'fill-in',
      instructions: 'Fill in the blank with an appropriate example-introduction phrase. Several answers may work; provide the most natural one.',
      questions: [
        {
          text: 'Technology has changed daily life enormously. ___, most people now use their phones to pay for things instead of carrying cash.',
          answer: 'For instance',
          explanation: '"For instance" is a versatile neutral phrase that works naturally here. "For example" would also be acceptable.',
        },
        {
          text: 'There are many ways to stay healthy. You could exercise regularly, eat well, or manage stress — ___ yoga or meditation.',
          answer: 'such as',
          explanation: '"Such as" fits because we are listing specific items within the sentence.',
        },
        {
          text: 'I believe reading is the best way to improve vocabulary. ___, I learned the word "serendipity" from a novel I read last summer.',
          answer: 'To give you an idea',
          explanation: '"To give you an idea" introduces a personal illustration naturally. "For example" or "In my own experience" would also work.',
        },
        {
          text: 'Urbanisation causes many environmental problems. ___ how Beijing struggles with air pollution every winter.',
          answer: 'Take',
          explanation: '"Take ... for example" is the pattern here. The sentence naturally continues with a specific case after "Take".',
        },
      ],
    },
    {
      title: 'Identify Weak vs Strong Examples',
      type: 'select',
      instructions: 'Decide whether the example effectively supports the speaker\'s point.',
      questions: [
        {
          text: 'Claim: "Social media can be harmful to teenagers." Example: "For instance, my cousin spent so much time on Instagram that her grades dropped significantly last semester." Is this a strong example?',
          options: ['Strong — specific and relevant', 'Weak — too vague', 'Weak — off-topic'],
          answer: 'Strong — specific and relevant',
          explanation: 'This example is specific (cousin, Instagram, grades dropped) and directly supports the claim about harm to teenagers.',
        },
        {
          text: 'Claim: "Public transport should be free." Example: "For example, transport is really expensive these days." Is this strong?',
          options: ['Strong — specific and relevant', 'Weak — it restates the claim, not an example', 'Weak — off-topic'],
          answer: 'Weak — it restates the claim, not an example',
          explanation: 'This merely rephrases the point rather than providing a concrete instance. A stronger example would cite a specific city or policy.',
        },
        {
          text: 'Claim: "Learning a language opens career opportunities." Example: "To illustrate, my friend secured a marketing role at a French firm specifically because she was fluent in French." Strong or weak?',
          options: ['Strong — specific and relevant', 'Weak — too vague', 'Weak — off-topic'],
          answer: 'Strong — specific and relevant',
          explanation: 'The example gives a concrete person, a specific language, and a direct career outcome — exactly what the claim needs.',
        },
        {
          text: 'Claim: "Exercise improves mental health." Example: "For example, I like pizza and sometimes I go swimming." Strong or weak?',
          options: ['Strong — specific and relevant', 'Weak — off-topic and disconnected', 'Weak — it restates the claim'],
          answer: 'Weak — off-topic and disconnected',
          explanation: 'The pizza reference is irrelevant, and "sometimes I go swimming" doesn\'t explicitly connect to mental health improvement.',
        },
      ],
    },
    {
      title: 'Add Examples to Answers',
      type: 'writing',
      instructions: 'Read the incomplete IELTS answer below and write 1–2 sentences adding a concrete example. Use a varied example phrase.',
      questions: [
        {
          text: '"I think travelling broadens your perspective. ______"',
          example: '"I think travelling broadens your perspective. For instance, when I visited Morocco last year, I discovered a completely different approach to hospitality — strangers invited us for tea, which taught me a lot about generosity."',
          tip: 'Choose a specific place and a specific thing you learned. Avoid vague statements like "you learn about culture."',
        },
        {
          text: '"Some people believe children should learn to cook at school. ______"',
          example: '"Some people believe children should learn to cook at school. A good example of this is Jamie Oliver\'s campaign in the UK, which introduced cooking classes into primary schools and led to healthier eating habits among students."',
          tip: 'You can use a well-known case or a personal story — both work. Make sure the example directly supports the idea of children learning to cook.',
        },
        {
          text: '"Technology has made communication faster but sometimes less meaningful. ______"',
          example: '"Technology has made communication faster but sometimes less meaningful. In my own experience, I often send quick text messages to friends instead of calling, and I\'ve noticed our conversations feel more superficial as a result."',
          tip: 'Contrast speed with depth. A personal observation about texting vs calling is ideal here.',
        },
      ],
    },
    {
      title: 'Match Formality Level',
      type: 'select',
      instructions: 'Select the phrase that matches the required formality level for each situation.',
      questions: [
        {
          text: 'Part 3 discussion about government policy (formal): Which phrase is most appropriate?',
          options: ['Like, you know...', 'One case in point is...', 'So basically...'],
          answer: 'One case in point is...',
          explanation: '"One case in point is" matches the formal register expected in a Part 3 policy discussion.',
        },
        {
          text: 'Part 1 question about hobbies (informal): Which phrase sounds most natural?',
          options: ['To illustrate this phenomenon,', 'A prime example would be', 'Take painting, for example —'],
          answer: 'Take painting, for example —',
          explanation: '"Take painting, for example" sounds natural and conversational, fitting the informal tone of Part 1.',
        },
        {
          text: 'Part 2 long turn about a memorable event (neutral): Which phrase works best to introduce a detail?',
          options: ['For instance,', 'A case in point is', 'According to research,'],
          answer: 'For instance,',
          explanation: '"For instance" is versatile and neutral, working perfectly in a Part 2 narrative without sounding too formal or too casual.',
        },
      ],
    },
  ],

  tips: [
    'State your main idea first, then follow with an example — never lead with an example alone.',
    'Keep examples to 1–2 sentences; long anecdotes risk losing the examiner\'s attention.',
    'Vary your example phrases across Parts 1, 2, and 3 to show lexical range.',
    'Blend personal and general examples in Part 3 for a well-rounded answer.',
    'If you can\'t think of a real example, invent a plausible one — the examiner assesses language, not truth.',
  ],
  dailyPractice: [
    'Pick one news article and practise explaining its main point with a supporting example using a different phrase each time.',
    'Record yourself answering a Part 2 cue card and count how many example phrases you use — aim for at least three varied ones.',
    'Listen to a podcast or TED talk and note every time the speaker introduces an example. Write down the phrase they used.',
    'Practise turning vague statements into specific ones by adding a who, what, when, or where detail.',
  ],
  backPath: '/exams/english/#speaking',
  backLabel: 'Back to Speaking',
  aiContext: 'Using Examples Effectively: This course teaches IELTS Speaking candidates how to support their ideas with concrete examples. Covers introduction phrases ranked by formality, personal vs general examples, keeping examples concise and relevant, and avoiding common repetition mistakes. Targets Band 6–7+ improvement in Coherence & Cohesion and Lexical Resource.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...usingExamplesData,
      title: usingExamplesData.title + ' (Foundations)',
      overview: { ...usingExamplesData.overview, duration: '15-20 minutes' },
      sections: usingExamplesData.sections.slice(0, Math.max(2, Math.ceil(usingExamplesData.sections.length / 2))),
      exercises: usingExamplesData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...usingExamplesData,
      title: usingExamplesData.title + ' (Advanced)',
      overview: { ...usingExamplesData.overview, duration: '30-35 minutes' },
      sections: usingExamplesData.sections,
      exercises: usingExamplesData.exercises.map(ex => ({ ...ex, title: ex.title + ' (Advanced)' })),
      tips: [
        ...(usingExamplesData.tips || []),
        'For Band 8+: Focus on natural-sounding delivery rather than just accuracy.',
        'Advanced tip: Integrate this skill naturally in timed IELTS speaking practice.',
      ],
    };
  }
  return usingExamplesData;
}
